import React, { useState } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import { getCommentsWithPagination, deleteComment, updateCommentStatus } from "adminApi/BlogApi/BlogComment";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import { showSuccess, showError } from "utils/notifications";

// Comment table columns defined inline — no add/edit form exists for comments
const tabledata = [
    { name: "blog_title", label: "Blog", type: "text" },
    { name: "comment", label: "Comment", type: "text" },
    { name: "customer_name", label: "Customer", type: "text" },
    { name: "status", label: "Status", type: "text" },
    { name: "created_at", label: "Date", type: "text" },
];

const STATUS_MAP = {
    0: { label: "Rejected", cls: "badge bg-danger" },
    1: { label: "Approved", cls: "badge bg-success" },
};

const BlogCommentTable = () => {
    const [search, setSearch] = useState("");
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1);
    const debouncedSearch = useDebouncedValue(search, 500);
    const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);
    const sizeOptions = [10, 20, 50, 100];

    const { data, meta, loading, error, refetch } = usePaginatedFetch(getCommentsWithPagination, {
        page,
        limit,
        search: debouncedSearch,
        title: "Blog Comments",
    });

    // Build display fields with a custom status badge renderer
    const displayFields = tabledata.map((f) => ({
        Header: f.label,
        accessor: f.name,
        dataKey: f.name,
        ...(f.name === "status" && {
            Cell: ({ value }) => {
                const s = STATUS_MAP[value] ?? STATUS_MAP[0];
                return <span className={s.cls}>{s.label}</span>;
            },
        }),
    }));

    // Build formattedData manually (no useTableConfig — custom action column)
    const formattedData = data.map((item, index) => {
        const row = { rowNo: (page - 1) * limit + index + 1 };
        tabledata.forEach((f) => { row[f.name] = item[f.name]; });
        row.id = item.id;
        return row;
    });

    const handleStatusChange = async (id, status) => {
        const res = await updateCommentStatus({ id, data: { status } });
        if (res?.data?.success) {
            showSuccess("Comment status updated");
            refetch();
        } else {
            showError(res?.data?.error || "Failed to update status");
        }
    };

    const handleDelete = async (id) => {
        const res = await deleteComment({ id });
        if (res?.data?.success) {
            showSuccess("Comment deleted");
            refetch();
        } else {
            showError(res?.data?.error || "Failed to delete comment");
        }
    };

    const baseColumns = [
        { Header: "No", accessor: "rowNo" },
        ...displayFields,
    ];

    const actionColumn = {
        Header: "Action",
        accessor: "action",
        sortable: false,
        Cell: ({ row }) => {
            const id = row.original.id;
            const currentStatus = row.original.status;
            return (
                <div className="d-flex gap-1 flex-wrap">
                    {currentStatus !== 1 && (
                        <button
                            className="btn btn-sm btn-success"
                            title="Approve"
                            onClick={() => handleStatusChange(id, 1)}
                        >
                            <i className="bi bi-check-lg" />
                        </button>
                    )}
                    {currentStatus !== 0 && (
                        <button
                            className="btn btn-sm btn-danger"
                            title="Reject"
                            onClick={() => handleStatusChange(id, 0)}
                        >
                            <i className="bi bi-x-lg" />
                        </button>
                    )}
                    <button
                        className="btn btn-sm btn-secondary"
                        title="Delete"
                        onClick={() => handleDelete(id)}
                    >
                        <i className="bi bi-trash" />
                    </button>
                </div>
            );
        },
    };

    const columns = canShowAction ? [...baseColumns, actionColumn] : baseColumns;

    return (
        <DataTable
            columns={columns}
            data={formattedData}
            options={{
                loading,
                error,
                sortableColumns: ["blog_title", "status", "created_at"],
                copy: true,
                csv: true,
                pdf: true,
                print: true,
                fileName: "Blog Comments",
            }}
            Search={{
                search: true,
                SearchInputValue: search,

                handleSearch: (val, resetPage = null) => {
                    setSearch(val);
                    if (resetPage !== null) setPage(resetPage);
                },
            }}
            Entries={{
                Entries: true,
                limit,
                onLimitChange: (newLimit) => { setLimit(newLimit); setPage(1); },
                options: sizeOptions,
            }}
            pagination={{
                isActive: true,
                meta,
                onPageChange: (p) => setPage(p),
            }}
        />
    );
};

export default BlogCommentTable;
